/**
 * Indicateurs : progression, pagination et paires libelle / valeur. Les
 * valeurs sont annoncees aux lecteurs d'ecran (role, aria-live), pas seulement
 * dessinees.
 */
import { useId } from 'react';
import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Info as IconeInfo } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Bouton } from './boutons';
import { Infobulle } from './menus';

/** Barre de progression accessible (role et valeurs annoncees). */
export function BarreProgression({
  valeur,
  libelle,
  ton = 'accent',
  className = '',
}: {
  /** Pourcentage, borne a [0, 100]. */
  valeur: number;
  libelle: string;
  ton?: 'accent' | 'succes' | 'danger';
  className?: string;
}) {
  const borne = Math.min(100, Math.max(0, Number.isFinite(valeur) ? valeur : 0));
  const remplissage =
    ton === 'succes' ? 'bg-succes-600' : ton === 'danger' ? 'bg-danger-600' : 'bg-accent-600';
  return (
    <div
      role="progressbar"
      aria-label={libelle}
      aria-valuenow={Math.round(borne)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={`h-2 w-full overflow-hidden rounded-full bg-surface-appuyee ${className}`}
    >
      <div
        className={`h-full rounded-full transition-[width] duration-150 ${remplissage}`}
        style={{ width: `${borne}%` }}
      />
    </div>
  );
}

export function Pagination({
  page,
  nbPages,
  total,
  taillePage,
  onPage,
  onTaillePage,
  taillesDisponibles,
  occupe = false,
}: {
  page: number;
  nbPages: number;
  total: number;
  taillePage: number;
  onPage: (page: number) => void;
  onTaillePage: (taille: number) => void;
  taillesDisponibles: readonly number[];
  /**
   * Chiffres d'une vue gardee le temps d'en lire une autre : les pages ne se changent pas depuis elle. La taille de
   * page reste active, elle relit la vue demandee.
   */
  occupe?: boolean;
}) {
  const premier = total === 0 ? 0 : (page - 1) * taillePage + 1;
  const dernier = Math.min(total, page * taillePage);
  const idTaille = useId();
  const versPrecedente = page > 1 && !occupe;
  const versSuivante = page < nbPages && !occupe;

  return (
    <nav
      aria-label="Pagination des résultats"
      aria-busy={occupe || undefined}
      className="flex flex-wrap items-center justify-between gap-3 border-t border-bordure bg-surface-douce px-4 py-3 text-sm text-encre-douce"
    >
      <p className="tabular-nums">
        {total === 0 ? 'Aucun résultat' : `${premier} – ${dernier} sur ${total}`}
      </p>
      {/* Sous 640 px, la taille de page et la navigation passent sur deux lignes : sur une seule, elles
          deborderaient du panneau d'un telephone. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:flex-nowrap">
        <div className="flex items-center gap-2">
          <label htmlFor={idTaille} className="hidden sm:inline">
            Par page
          </label>
          <select
            id={idTaille}
            className="champ h-7 w-auto py-0 text-xs"
            value={taillePage}
            onChange={(evenement) => onTaillePage(Number(evenement.target.value))}
          >
            {taillesDisponibles.map((taille) => (
              <option key={taille} value={taille}>
                {taille}
              </option>
            ))}
          </select>
        </div>
        {/* Aux bornes et pendant une lecture, `aria-disabled` et non `disabled` : le bouton qui vient de servir garde le focus. */}
        <div className="flex items-center gap-2">
          <Bouton
            taille="sm"
            icone={<ChevronLeft />}
            onClick={() => {
              if (versPrecedente) onPage(page - 1);
            }}
            aria-disabled={!versPrecedente}
            aria-label="Page précédente"
          >
            Précédente
          </Bouton>
          <span aria-live="polite" className="tabular-nums">
            Page {page} / {Math.max(1, nbPages)}
          </span>
          <Bouton
            taille="sm"
            onClick={() => {
              if (versSuivante) onPage(page + 1);
            }}
            aria-disabled={!versSuivante}
            aria-label="Page suivante"
          >
            Suivante
            <ChevronRight />
          </Bouton>
        </div>
      </div>
    </nav>
  );
}

/** Petit indicateur textuel neutre (compteurs, metadonnees). */
export function Info({ libelle, children }: { libelle: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-encre-discrete">{libelle}</dt>
      <dd className="mt-1 text-base text-encre">{children}</dd>
    </div>
  );
}

export type TonTuile = 'neutre' | 'accent' | 'succes' | 'avertissement' | 'danger' | 'info';

const TONS_TUILE: Record<TonTuile, { icone: string; valeur: string }> = {
  neutre: { icone: 'bg-surface-appuyee text-encre-douce', valeur: 'text-encre' },
  accent: { icone: 'bg-accent-50 text-accent-700', valeur: 'text-encre' },
  succes: { icone: 'bg-succes-50 text-succes-700', valeur: 'text-succes-700' },
  avertissement: { icone: 'bg-avertissement-50 text-avertissement-700', valeur: 'text-avertissement-700' },
  danger: { icone: 'bg-danger-50 text-danger-700', valeur: 'text-danger-700' },
  info: { icone: 'bg-info-50 text-info-700', valeur: 'text-encre' },
};

/**
 * Indicateur chiffre. Avec `vers`, toute la tuile mene a la liste
 * correspondante : le lien porte le libelle (nom accessible explicite) et
 * s'etire sur la tuile, ce qui laisse le bouton d'aide cliquable par-dessus.
 */
export function TuileStat({
  libelle,
  valeur,
  indice,
  ton = 'neutre',
  icone: Icone,
  vers,
  aide,
}: {
  libelle: string;
  valeur: ReactNode;
  indice?: ReactNode;
  ton?: TonTuile;
  icone?: LucideIcon;
  vers?: string;
  /** Definition de l'indicateur, lue au survol comme au focus clavier. */
  aide?: string;
}) {
  const styles = TONS_TUILE[ton];
  return (
    <div className="panneau relative flex flex-col gap-3 p-4 transition-shadow has-[a:hover]:shadow-releve">
      <div className="flex items-start justify-between gap-3">
        <p className="flex items-center gap-1.5 text-sm font-medium text-encre-douce">
          {vers ? (
            <Link
              to={vers}
              className="after:absolute after:inset-0 after:rounded-lg hover:text-encre focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-accent-600"
            >
              {libelle}
            </Link>
          ) : (
            libelle
          )}
          {aide && (
            <Infobulle contenu={aide} className="relative z-10">
              <button
                type="button"
                aria-label={`Comment est calculé « ${libelle} » ?`}
                className="rounded-full text-encre-tenue hover:text-encre-douce"
              >
                <IconeInfo aria-hidden="true" size={14} />
              </button>
            </Infobulle>
          )}
        </p>
        {Icone && (
          <span
            aria-hidden="true"
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${styles.icone}`}
          >
            <Icone />
          </span>
        )}
      </div>
      <p className={`text-2xl font-semibold tracking-titre ${styles.valeur}`}>{valeur}</p>
      {indice && <p className="text-xs text-encre-discrete">{indice}</p>}
    </div>
  );
}
